import { AgGridReact } from 'ag-grid-react';
import { themeQuartz, type ColDef, type GridOptions } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useRef, useMemo, useCallback } from 'react';
import type { RootState } from '../../../../state/store';

interface ISubRTableViewProps {
  data: {
    config: {
      widget_name: string;
      currency_code?: string;
      currency_precision?: number;
    };
    columns: Array<{
      header: string;
      accessor: string;
      formatType?: string;
    }>;
    data: any[];
    total?: {
      label: string;
      value: string;
    };
  };
}

const SubRTableView = ({ data }: ISubRTableViewProps) => {
  const theme = useSelector((state: RootState) => state.theme.mode);
  const gridRef = useRef<any>(null);
  const initialAutoSizeCompleteRef = useRef<boolean>(false);

  // Helper function to determine if a value is numeric
  const isNumericValue = useCallback((value: any) => {
    return typeof value === 'number' || (typeof value === 'string' && !isNaN(parseFloat(value)));
  }, []);

  // Helper function to determine if a column is a currency column
  const isCurrencyColumn = useCallback((accessor: string) => {
    return (
      accessor.toLowerCase().includes('total') ||
      accessor.toLowerCase().includes('amount') ||
      accessor.toLowerCase().includes('cost') ||
      accessor.toLowerCase().includes('price')
    );
  }, []);

  const columnDefs = useMemo(() => {
    if (!data.data || data.data.length === 0 || !data.columns) return [];

    return data.columns.map((column) => {
      const colDef: ColDef = {
        field: column.accessor,
        headerName: column.header,
        minWidth: column.accessor === 'sr_no' ? 60 : 150,
        suppressSizeToFit: false,
        headerClass: 'centered-header',
        flex: column.accessor === 'sr_no' ? 0 : 1 // Allow columns to flex except sr_no
      };

      // Special handling for sr_no column
      if (column.accessor === 'sr_no') {
        colDef.pinned = 'left' as const;
        colDef.sortable = false;
        colDef.filter = false;
        colDef.resizable = false;
        colDef.suppressHeaderMenuButton = true;
        colDef.lockPinned = true;
        colDef.lockVisible = true;
        colDef.lockPosition = true;
        colDef.suppressMovable = true;
        colDef.suppressColumnsToolPanel = true;
        colDef.suppressSizeToFit = true;
        colDef.valueFormatter = (params: any) => {
          // For pinned bottom row (total row), return the value as is
          if (params.node?.rowPinned === 'bottom') {
            return params.value;
          }
          // For regular rows, return the row index + 1
          return params.node?.rowIndex !== undefined && params.node?.rowIndex !== null
            ? (params.node.rowIndex + 1).toString()
            : '';
        };
        colDef.cellStyle = (params: any) => {
          const isTotal = params.node?.rowPinned === 'bottom';
          return {
            textAlign: 'center',
            backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa',
            borderRight: theme === 'dark' ? '1px solid #404040' : '1px solid #dee2e6',
            fontWeight: isTotal ? '700' : '500',
            color: theme === 'dark' ? '#e5e5e5' : '#495057'
          };
        };
      }

      // Determine cell alignment based on content type and field name
      const firstRowValue = data.data[0]?.[column.accessor];
      const isNumeric = isNumericValue(firstRowValue);
      const isCurrency = isCurrencyColumn(column.accessor);

      colDef.cellClass = isNumeric || isCurrency ? 'ag-cell-right-align' : 'ag-cell-left-align';

      // Special handling for currency and numeric values
      if (isCurrency && data.config.currency_code && data.config.currency_precision !== undefined) {
        colDef.valueFormatter = (params: any) => {
          if (params.value == null) return '';
          const numValue =
            typeof params.value === 'number' ? params.value : parseFloat(params.value);
          return isNaN(numValue) ? params.value : numValue.toFixed(data.config.currency_precision);
        };
      }

      // Handle negative formatType - apply red color to negative values and format with parentheses
      if (column.formatType === 'negative') {
        const existingCellStyle = colDef.cellStyle;
        const existingValueFormatter = colDef.valueFormatter;

        // Add valueFormatter to remove negative sign and wrap in parentheses
        colDef.valueFormatter = (params: any) => {
          // Check if value is negative
          const numValue =
            typeof params.value === 'number'
              ? params.value
              : typeof params.value === 'string'
                ? parseFloat(params.value)
                : params.value;

          if (params.value != null && !isNaN(numValue) && numValue < 0) {
            // If there's an existing formatter (like currency), apply it to absolute value
            if (existingValueFormatter && typeof existingValueFormatter === 'function') {
              const absoluteValue = Math.abs(numValue);
              const tempParams = { ...params, value: absoluteValue };
              const absFormatted = existingValueFormatter(tempParams);
              return `(${absFormatted})`;
            } else {
              // No existing formatter, format the absolute value
              const absoluteValue = Math.abs(numValue);
              return `(${absoluteValue.toLocaleString()})`;
            }
          }

          // For non-negative values, apply existing formatter if it exists
          if (existingValueFormatter && typeof existingValueFormatter === 'function') {
            return existingValueFormatter(params);
          }
          return params.value;
        };

        colDef.cellStyle = (params: any) => {
          // Get base style from existing cellStyle if it exists
          const baseStyle = existingCellStyle
            ? typeof existingCellStyle === 'function'
              ? existingCellStyle(params)
              : existingCellStyle
            : {};

          // Check if value is negative
          const numValue =
            typeof params.value === 'number'
              ? params.value
              : typeof params.value === 'string'
                ? parseFloat(params.value)
                : params.value;

          if (params.value != null && !isNaN(numValue) && numValue < 0) {
            return {
              ...baseStyle,
              color: '#dc3545',
              fontWeight: 'bold'
            };
          }

          return baseStyle;
        };
      }

      return colDef;
    });
  }, [data, theme, isNumericValue, isCurrencyColumn]);

  const defaultColDef: ColDef = useMemo(
    () => ({
      sortable: false,
      filter: false,
      resizable: true,
      suppressHeaderMenuButton: true,
      headerClass: 'centered-header',
      autoHeight: false,
      wrapText: false,
      cellClass: (params: any) => {
        const classes = [];

        // Add alignment class based on content type
        const isNumeric = isNumericValue(params.value);
        const isCurrency = isCurrencyColumn(params.colDef.field);

        classes.push(isNumeric || isCurrency ? 'ag-cell-right-align' : 'ag-cell-left-align');

        // Add total row styling
        if (params.node.rowPinned === 'bottom') {
          classes.push('total-row');
        }

        return classes.join(' ');
      },
      cellStyle: (params: any) => {
        if (params.node.rowPinned === 'bottom') {
          const baseStyle = {
            backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa',
            borderTop: theme === 'dark' ? '1px solid #404040' : '1px solid #dee2e6',
            fontWeight: '600',
            color: theme === 'dark' ? '#e5e5e5' : '#495057'
          };

          // Special styling for total amount and label cells
          if (params.value && params.value !== '') {
            return {
              ...baseStyle,
              fontWeight: '700',
              backgroundColor: theme === 'dark' ? '#333333' : '#e6e6e6'
            };
          }

          return baseStyle;
        }
        return null;
      }
    }),
    [theme, isNumericValue, isCurrencyColumn]
  );

  const reportTheme = useMemo(() => {
    return theme === 'dark'
      ? themeQuartz.withParams({
          accentColor: '#0D8A6A',
          backgroundColor: '#1a1a1a',
          borderColor: '#404040',
          borderRadius: 4,
          browserColorScheme: 'dark',
          cellTextColor: '#e5e5e5',
          columnBorder: true,
          fontSize: 14,
          foregroundColor: '#e5e5e5',
          headerBackgroundColor: '#2d2d2d',
          headerFontSize: 14,
          headerTextColor: '#ffffff',
          oddRowBackgroundColor: '#262626',
          wrapperBorderRadius: 8
        })
      : themeQuartz.withParams({
          accentColor: '#0D8A6A',
          backgroundColor: '#f4f6f6',
          borderRadius: 4,
          browserColorScheme: 'inherit',
          fontSize: 14,
          headerFontSize: 14,
          headerTextColor: '#374151',
          wrapperBorderRadius: 8,
          borderColor: '#d1d5db',
          columnBorder: true,
          headerBackgroundColor: '#d8edea',
          oddRowBackgroundColor: '#ffffff',
          cellTextColor: '#374151',
          foregroundColor: '#374151'
        });
  }, [theme]);

  // Create pinned bottom row for total if total is provided
  const pinnedBottomRow = useMemo(() => {
    if (!data.total) return undefined;

    // Create a row with all fields empty initially
    const totalRow: { [key: string]: any } = {};
    data.columns.forEach((col) => {
      totalRow[col.accessor] = '';
    });

    // Find the total amount column
    const totalAmountColumn = data.columns.find(
      (col) => col.accessor === 'total_amount' || col.accessor === 'total'
    );

    if (totalAmountColumn) {
      totalRow[totalAmountColumn.accessor] = data.total.value;
      // Put the "Total" label in the sr_no column
      totalRow.sr_no = data.total.label;
    }
    return [totalRow];
  }, [data.total, data.columns]);

  const handleGridReady = useCallback((params: any) => {
    gridRef.current = params.api;
    if (!initialAutoSizeCompleteRef.current) {
      setTimeout(() => {
        params.api.sizeColumnsToFit();
        initialAutoSizeCompleteRef.current = true;
      }, 200);
    }
  }, []);

  const handleFirstDataRendered = useCallback((params: any) => {
    if (!initialAutoSizeCompleteRef.current) {
      setTimeout(() => {
        params.api.sizeColumnsToFit();
        params.api.refreshHeader();
        initialAutoSizeCompleteRef.current = true;
      }, 200);
    }
  }, []);

  const handleColumnVisible = useCallback(() => {
    // Recalculate table height after column visibility changes
    if (gridRef.current) {
      setTimeout(() => {
        gridRef.current.sizeColumnsToFit();
      }, 100);
    }
  }, []);

  const handleColumnMoved = useCallback(() => {
    // Ensure proper height is maintained after column reordering
    if (gridRef.current) {
      setTimeout(() => {
        gridRef.current.sizeColumnsToFit();
      }, 100);
    }
  }, []);

  const gridOptions = useMemo<GridOptions>(
    () => ({
      animateRows: true,
      enableRangeSelection: false,
      enableCharts: false,
      suppressMenuHide: true,
      pagination: false,
      enableCellTextSelection: false,
      cellSelection: true,
      rowSelection: 'multiple',
      allowContextMenu: false,
      suppressCsvExport: true,
      suppressExcelExport: true,
      suppressContextMenu: false,
      domLayout: 'autoHeight',
      headerHeight: 32,
      rowHeight: 40,
      getRowHeight: () => 40,
      suppressHorizontalScroll: false,
      suppressVerticalScroll: false,
      ensureDomOrder: true,
      suppressColumnVirtualisation: true,
      alwaysShowHorizontalScroll: false,
      alwaysShowVerticalScroll: false,
      suppressScrollOnNewData: true,
      pinnedBottomRowData: pinnedBottomRow,
      popupParent: document.body,
      onGridReady: handleGridReady,
      onFirstDataRendered: handleFirstDataRendered,
      onColumnVisible: handleColumnVisible,
      onColumnMoved: handleColumnMoved
    }),
    [
      pinnedBottomRow,
      handleGridReady,
      handleFirstDataRendered,
      handleColumnVisible,
      handleColumnMoved
    ]
  );

  if (!data.data || data.data.length === 0) {
    return (
      <NoDataContainer>
        <NoDataMessage>No Data Available</NoDataMessage>
      </NoDataContainer>
    );
  }

  return (
    <TableContainer>
      <GridContainer>
        <AgGridReact
          ref={gridRef}
          theme={reportTheme}
          rowData={data.data}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          gridOptions={gridOptions}
        />
      </GridContainer>
    </TableContainer>
  );
};

export default SubRTableView;

const TableContainer = styled.div`
  position: relative;
  height: auto;
  width: 100%;
  max-width: 100vw;
  overflow: visible;

  .table-container {
    position: relative;
    height: auto;
    min-height: 0;
    padding-bottom: 0;
    margin-bottom: 0;
    width: 100%;
  }

  .ag-theme-quartz {
    .ag-root-wrapper {
      border: none;
      border-radius: 0;
    }

    .ag-root {
      border: none;
    }

    .ag-body {
      min-height: 100px;
    }

    &.ag-layout-auto-height {
      .ag-center-cols-container,
      .ag-center-cols-viewport {
        min-height: 100px !important;
      }
    }
  }
`;

const GridContainer = styled.div`
  height: auto;
  width: 100%;
  position: relative;
  overflow: visible;
  display: block;
  margin: 0;
  padding: 0;
  min-width: 100%;
  box-sizing: border-box;

  .ag-theme-quartz.ag-layout-auto-height {
    .ag-center-cols-container {
      min-height: 100px !important;
    }

    .ag-center-cols-viewport {
      min-height: 100px !important;
    }
  }

  .ag-layout-auto-height .ag-center-cols-container,
  .ag-layout-auto-height .ag-center-cols-viewport {
    min-height: 100px !important;
  }

  .ag-root {
    margin-bottom: 0;
  }

  .ag-root-wrapper {
    border-bottom: none;
    min-height: 100px;
  }

  .ag-body-viewport {
    min-height: 100px;
  }

  .ag-header-cell-label {
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    width: 100% !important;
  }

  .ag-header-cell-text {
    text-align: center !important;
    width: 100% !important;
    display: block !important;
  }

  .ag-header-cell-comp-wrapper {
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    width: 100% !important;
  }

  .ag-header-cell {
    .ag-header-cell-label {
      justify-content: center !important;
    }
  }

  .ag-header-cell .ag-react-container {
    width: 100% !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
  }

  .ag-cell-right-align {
    justify-content: flex-end;
    text-align: right;
    white-space: nowrap;
    padding: 8px;
  }

  .ag-cell-left-align {
    justify-content: flex-start;
    text-align: left;
    white-space: nowrap;
    padding: 8px;
  }

  .ag-cell-center-align {
    justify-content: center;
    text-align: center;
    white-space: nowrap;
    padding: 8px;
  }

  .ag-header-cell {
    overflow: visible !important;
  }

  &.ag-layout-auto-height,
  .ag-layout-auto-height,
  .ag-layout-print {
    .ag-center-cols-container,
    .ag-center-cols-viewport {
      min-height: 100px !important;
      height: auto !important;
    }
  }
`;

const NoDataContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  width: 100%;
`;

const NoDataMessage = styled.p`
  text-align: center;
  color: #6b7280;
  font-size: 16px;
`;
